const triggerAnchorDownload = (url: string, filename: string, openInNewTab = false) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  if (openInNewTab) {
    a.target = "_blank";
    a.rel = "noopener";
  }
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const downloadFile = (url: string, filename: string) => triggerAnchorDownload(url, filename);

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  triggerAnchorDownload(url, fileName);
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export type DownloadPlan =
  | { kind: "blob", blob: Blob, fileName: string }
  | { kind: "navigate", url: string, fileName: string };

/**
 * Functional core: decides how a remote file should be downloaded.
 *
 * Tries to fetch it as a blob (which guarantees a real download with no new
 * tab). When the fetch fails — typically because S3 CORS blocks it or the
 * presigned URL has expired — it falls back to a plain navigation, relying on
 * the server's `Content-Disposition: attachment` header. Returns a plain
 * descriptor so the decision can be unit-tested via state assertions.
 */
export const resolveDownload = async (url: string, fileName: string): Promise<DownloadPlan> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    return { kind: "blob", blob: await response.blob(), fileName };
  }
  catch {
    return { kind: "navigate", url, fileName };
  }
};

/**
 * Imperative shell: performs the download side effect from a resolved plan.
 */
export const downloadUrlAsBlob = async (url: string, fileName: string) => {
  const plan = await resolveDownload(url, fileName);
  if (plan.kind === "blob") {
    downloadBlob(plan.blob, plan.fileName);
  }
  else {
    triggerAnchorDownload(plan.url, plan.fileName, true);
  }
};
